import { faCircleQuestion as fasCircleQuestion } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatText } from "@ui/format";
import autobind from "autobind-decorator";
import { FormCurrency, FormNumber } from "bm-ui-kit";
import React from "react";
import type { ReactNode } from "react";
import { Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import type { RetailAccountSearchParams } from "../types";

interface Props {
	label: ReactNode;
	name: string;
	info?: string;
	disabled?: boolean;
	maxCount?: number;
}

const MAX_COST_COUNT = 50;
const MAX_COST_AMOUNT = 20_000_000;
const MAX_CASH_WITHDAWAL_AMOUNT = 2_000_000;

export class CountAmountField extends React.Component<Props> {
	@autobind
	private renderTooltip(props): ReactNode {
		const { info } = this.props;

		return <Tooltip {...props}>{info}</Tooltip>;
	}

	public render(): ReactNode {
		const { label, name, info, disabled, maxCount = MAX_COST_COUNT } = this.props;

		return (
			<div className="mb-3">
				{info ? (
					<OverlayTrigger placement="top" overlay={this.renderTooltip}>
						<label className="form-label">
							{label}
							<span className="text-nowrap">
								&nbsp;
								<FontAwesomeIcon icon={fasCircleQuestion} />
							</span>
						</label>
					</OverlayTrigger>
				) : (
					<label className="form-label">{label}</label>
				)}

				<Row className="align-items-baseline">
					<Col sm={4}>
						<FormNumber
							className="mb-sm-0 mb-2"
							name={`${name}Count`}
							append="db"
							validate={this.validCount}
							disabled={disabled}
							min={0}
							max={maxCount}
							required
						/>
					</Col>
					<Col className="text-sm-right pr-sm-0 pb-sm-0 pb-1" sm={2}>
						<span className="d-xl-none d-lg-inline d-md-none d-sm-inline d-none">össz.:</span>
						<span className="d-xl-inline d-lg-none d-md-inline d-sm-none d-inline">összesen:</span>
					</Col>
					<Col sm={6}>
						<FormCurrency
							className="mb-sm-0 mb-1"
							name={`${name}Amount`}
							validate={this.validAmount}
							disabled={disabled}
							required
						/>
					</Col>
				</Row>
			</div>
		);
	}

	@autobind
	private validCount(count: number): string {
		const { maxCount = MAX_COST_COUNT } = this.props;

		if (count < 0) {
			return "A megadott érték nem lehet negatív!";
		}

		if (count > maxCount) {
			return `Max. ${maxCount} db!`;
		}

		if (!Number.isInteger(count)) {
			return "Csak egész szám adható meg!";
		}
	}

	@autobind
	private validAmount(amount: number, values: RetailAccountSearchParams): string {
		const { name } = this.props;
		const count: number = values[`${name}Count`];

		if (amount < 0) {
			return "A megadott érték nem lehet negatív!";
		}

		if (count === 0 && amount !== 0) {
			return `Ha a darabszám 0, az összegnek is 0 Ft-nak kell lennie!`;
		}

		if (count !== 0 && amount === 0) {
			return `Ha az összeg 0 Ft, a darabszámnak is 0-nak kell lennie!`;
		}

		if (amount > MAX_COST_AMOUNT) {
			return `A megadható legnagyobb összeg ${formatText(MAX_COST_AMOUNT, "ft")}!`;
		}

		if (name === "cashWithdawalForeignAtmAmount" || name === "cashWithdawalOwnAtmAmount") {
			if (count * amount > MAX_CASH_WITHDAWAL_AMOUNT) {
				return `Átlépted a felvehető készpénzkeretet (${formatText(MAX_CASH_WITHDAWAL_AMOUNT, "ft")})!`;
			}
		}
	}
}
